import { inject, injectable, named } from 'inversify';
import i18next from 'i18next';

import ConfigService from '../../ConfigService';
import type { Config, Content, Menu } from '../../../../types/config';
import { ucFirst } from '../../../utils/formatting';
import AccountService from '../AccountService';

import StreamIQApiService from './StreamIQApiService';
import type { ListGroupsResponse, ListsResponse } from './types';
import StreamIQAccountService from './StreamIQAccountService';
import StreamIQData from './StreamIQData';

@injectable()
export default class StreamIQConfigService extends ConfigService {
  constructor(
    @inject(StreamIQData) protected readonly data: StreamIQData,
    @inject(StreamIQApiService) protected readonly apiService: StreamIQApiService,
    @inject(AccountService) @named('STREAMIQ') protected readonly accountService: StreamIQAccountService,
  ) {
    super();
  }

  formatSourceLocation = () => {
    return undefined;
  };

  loadConfig = async () => {
    const regionId = this.accountService.regionId || 'f27c3e84-ddc9-4782-8f65-d667bac657a8'; // Play NXT region
    const { siteName, logo, highlightColor, backgroundColor } = await this.accountService.getRegionConfigVariables(regionId);

    return {
      id: 'streamiq',
      siteName: siteName || 'StreamIQ Demo',
      description: 'Welcome to the StreamIQ Demo site',
      siteId: 'streamiq',
      assets: {
        banner: logo || 'https://cdn.vd-ott.eu/portal/acc/image/x2rjrnnt.png',
      },
      // populated after login
      content: [],
      menu: [],
      integrations: {
        streamiq: {},
      },
      styling: {
        footerText: '',
        highlightColor,
        backgroundColor,
      },
      features: {
        favoritesList: 'favorites',
        recommendationsPlaylist: 'recommendations',
        continueWatchingList: 'continueWatching',
        searchPlaylist: 'search',
      },
      custom: {
        disableShare: true,
      },
    } satisfies Config;
  };

  getMenuItems = async () => {
    const listGroups = await this.apiService.get<ListGroupsResponse>(`/layout-service/listGroups?regionId=${this.accountService.regionId}`);
    const filtered = listGroups.resources.filter((group) => group.alias !== 'home');

    const menuItems: Menu[] = filtered.map((group) => ({
      contentId: `listgroup_${group.id}`,
      type: 'media',
      label: ucFirst(group.name),
    }));

    // TV Guide based on available lineups
    const lineup = this.data.getLineup();
    if (lineup) {
      // position the TV Guide menu item at ordinal 0
      const tvGuideIndex = filtered.findIndex(({ ordinal }) => !ordinal || ordinal > 0);
      menuItems.splice(tvGuideIndex, 0, { contentId: `lineup_${lineup.id}`, type: 'playlist', label: lineup.name });
    }

    return menuItems;
  };

  getContentItems = async () => {
    const listGroups = await this.apiService.get<ListGroupsResponse>(`/layout-service/listGroups?regionId=${this.accountService.regionId}`);

    const homeListGroup = listGroups.resources.find((group) => group.alias === 'home');
    const homeLists = await this.apiService.get<ListsResponse>(`/layout-service/lists?listGroupId=${homeListGroup?.id}`);

    const content = homeLists.resources.map((list, index) => {
      if (list.personalised) {
        if (list.query.includes('session-service/bookmarks')) {
          return { type: 'continue_watching', title: list.name } as Content;
        }

        if (list.query.includes('crm-service/favouriteListItems')) {
          return { type: 'favorites', title: list.name } as Content;
        }
      }

      return {
        contentId: list.id,
        type: 'playlist',
        title: ucFirst(list.name),
        custom: index === 0 ? { layoutType: 'hero' } : list.customVariable ? JSON.parse(list.customVariable) : {},
      } as Content;
    });

    // Add favorites if it isn't added yet
    if (!content.some((list) => list.type === 'favorites')) {
      content.push({ type: 'favorites', title: i18next.t('user:favorites.title') } satisfies Content);
    }

    return content;
  };
}
